import { CONTEXT_PATTERN_SEPARATOR } from '@common/Copilot/CopilotAgentChat/utils';
import { CopilotGradientBackdrop } from '@common/Copilot/CopilotOverlay';
import DragDropFileUpload from '@common/FileUpload/DragDropFileUpload';
import { UserPreferenceKeys } from '@common/preferences/types';
import { useUserPreference } from '@common/preferences/userPreference';
import { Private_Routes } from '@common/url';
import { useUserWorkspace } from '@common/VerticalApps/hooks';
import { CopilotLogoXl40pxIcon } from '@prophecy/icons';
import { HOME_CHAT_THREAD_WIDTH } from '@prophecy/shared-core/agentChat/layout';
import { theme } from '@prophecy/theme';
import { AIChatInput } from '@prophecy/ui-v3/Input/ChatInput/AIChatInput';
import { StyledAIChatInputWrapper, StyledPlateContent } from '@prophecy/ui-v3/Input/ChatInput/styled';
import { Stack, StackItem } from '@prophecy/ui-v3/Layout';
import { Spinner } from '@prophecy/ui-v3/Spinner';
import { toast } from '@prophecy/ui-v3/Toast';
import { Display, Text } from '@prophecy/ui-v3/Typography';
import { FROZEN_EMPTY_ARRAY } from '@prophecy/utils/constants';
import { usePersistentCallback } from '@prophecy/utils/react/hooks';
import { nanoid } from 'nanoid';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import {
  useCreateHomeConversation,
  useDeleteHomeConversation,
  useHomeConversationContent,
  useHomeConversations,
  useRenameHomeConversation
} from './api';
import { ChatHeader } from './ChatHeader';
import { lastPromptAwaitsReply, mergeTranscripts, pendingRequests } from './chatTranscript';
import { AttachFilesButton, AttachmentChips } from './ComposerAttachments';
import { HOME_CHAT_TEST_IDS, RESUME_LAST_CONVERSATION_STATE } from './constants';
import { ConversationList } from './ConversationList';
import { conversationNameFromPrompt } from './conversationName';
import { useHomeChatMentions } from './homeChatMentions';
import { consumePendingHomeChatPrompt, peekPendingHomeChatPrompt } from './pendingHomeChatPrompt';
import { PendingUserInput } from './PendingUserInput';
import { QueuedHomeChatPromptsList } from './QueuedHomeChatPromptsList';
import { Transcript } from './Transcript';
import { useHomeChatAttachments } from './useHomeChatAttachments';
import { useHomeChatSession } from './useHomeChatSession';
import { useQueuedHomeChatDispatcher } from './useQueuedHomeChatDispatcher';
import { useQueuedHomeChatPrompts } from './useQueuedHomeChatPrompts';
import { useStickToBottom } from './useStickToBottom';
import { WidgetFullscreenContext } from './widgetFullscreen';

import type { HomeChatElement } from './chatTranscript';
import type { SendUserInputAnswer } from '@common/Copilot/CopilotAgentChat/agent-v4/v4-widgets/UserInputRequest/ClarifyingQuestionsWidget';
import type { ReactNode } from 'react';

const SIDEBAR_WIDTH = '264px';
const EMPTY_STATE_TEXT_WIDTH = '480px';
const EMPTY_STATE_LOGO_SIZE = 80;
const COMPOSER_MIN_HEIGHT = '80px';
// Claude's composer grows to 384px (`max-h-96`) before it scrolls.
const COMPOSER_TEXT_MAX_HEIGHT = '384px';
const FILE_CONTEXT_PREFIX = '@file:';
const COMPOSER_PLACEHOLDER = 'Ask about your data, upload a file or connect a warehouse';

const Page = styled(Stack)`
  height: 100%;
  min-height: 0;
  background: ${theme.colors.white};
`;

const Sidebar = styled(Stack)`
  width: ${SIDEBAR_WIDTH};
  min-width: ${SIDEBAR_WIDTH};
  min-height: 0;
  border-right: 1px solid ${theme.colors.gray200};
  background: ${theme.colors.gray25};
`;

const Main = styled(Stack)`
  position: relative;
  z-index: 0;
  min-width: 0;
  min-height: 0;
`;

// A fullscreen widget card fills this pane: below the header, above the composer.
const ThreadPane = styled(Stack)`
  position: relative;
  min-height: 0;
`;

// Blanks the transcript while a widget card is fullscreen over it.
const FullscreenStage = styled(Stack)`
  position: absolute;
  inset: 0;
  z-index: ${theme.zLayer.s};
  background: ${theme.colors.white};
`;

const Scroller = styled(Stack)`
  min-height: 0;
  overflow-y: auto;
`;

const Column = styled(Stack)`
  width: 100%;
  max-width: ${HOME_CHAT_THREAD_WIDTH};
  margin: 0 auto;
  padding: ${theme.spaces.x24} ${theme.spaces.x12} ${theme.spaces.x40};
`;

const ComposerArea = styled(Stack)`
  width: 100%;
  max-width: ${HOME_CHAT_THREAD_WIDTH};
  margin: 0 auto;
  padding-bottom: ${theme.spaces.x32};
`;

const Composer = styled(Stack)`
  width: 100%;
  border-radius: ${theme.radius.x8};
  box-shadow: ${theme.shadows.xl};

  & > ${StyledAIChatInputWrapper} ${StyledPlateContent} {
    // Pinned, not inherited: an empty line is just a bare <br>, and without an explicit line-height
    // a browser can size that a pixel off from a line that holds real text — so the box visibly
    // grew the instant you typed the first character, even though min-height never changed.
    font-size: ${theme.text.sm.fontSize};
    line-height: ${theme.text.sm.lineHeight};
    max-height: ${COMPOSER_TEXT_MAX_HEIGHT};
  }
`;

const EmptyStateText = styled(Stack)`
  max-width: ${EMPTY_STATE_TEXT_WIDTH};
  text-align: center;
`;

/** The composer, and — once a conversation has questions waiting — the pending-ask cards above it. */
function ComposerSection({
  pending,
  onAnswer,
  children
}: {
  pending?: HomeChatElement[];
  onAnswer?: SendUserInputAnswer;
  children: ReactNode;
}) {
  return (
    <ComposerArea gap={theme.spaces.x10}>
      {pending && onAnswer ? <PendingUserInput requests={pending} sendAnswer={onAnswer} /> : null}
      {children}
    </ComposerArea>
  );
}

export function HomeChatPage() {
  const { conversationId } = useParams<{ conversationId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  // Home's hand-off lands in the composer straight away, so it is never lost while it waits to send.
  const [handoff] = useState(() => (conversationId ? undefined : peekPendingHomeChatPrompt()));
  const [prompt, setPrompt] = useState(() => handoff?.prompt ?? '');
  const [scroller, setScroller] = useState<HTMLElement | null>(null);
  const [content, setContent] = useState<HTMLElement | null>(null);

  // Not the raw preference: that can be genuinely unset (never explicitly switched), where this
  // falls back to the user's first enabled team — same resolution the workspace switcher uses, and
  // synchronously correct, since `UserProvider` already blocks the app on teams + preferences loading.
  const { currentWorkspaceId: teamUID } = useUserWorkspace();
  const conversationsQuery = useHomeConversations(teamUID);
  const history = useHomeConversationContent(conversationId);
  const session = useHomeChatSession(conversationId, history.messages);
  // The broker connection, not the chat's pod: a pod is allocated by the chat's first prompt, so
  // a prompt only has to wait for the broker to be there to take it.
  const sandboxReady = session.sandbox?.state === 'ready';
  const queuedPrompts = useQueuedHomeChatPrompts(conversationId);
  const dispatcher = useQueuedHomeChatDispatcher({
    conversationId,
    isWorking: session.isWorking,
    isRunningElsewhere: session.isRunningElsewhere,
    historyLoading: history.isLoading,
    historyMessages: history.messages,
    sandboxReady,
    send: session.send,
    queuedPrompts
  });
  const { create, isCreating } = useCreateHomeConversation();
  const { rename, renamingId } = useRenameHomeConversation();
  const { remove, deletingId } = useDeleteHomeConversation();
  const attachments = useHomeChatAttachments();
  const mentions = useHomeChatMentions();
  const [widgetFullscreen, setWidgetFullscreen] = useState(false);
  const [storedPinnedIds, setPinnedIds] = useUserPreference(UserPreferenceKeys.homeChatPinnedConversations);
  const pinnedIds = storedPinnedIds ?? (FROZEN_EMPTY_ARRAY as string[]);
  const [lastConversationId, setLastConversationId] = useUserPreference(UserPreferenceKeys.homeChatLastConversation);

  const messages = useMemo(
    () => mergeTranscripts([history.messages, session.liveMessages]),
    [history.messages, session.liveMessages]
  );
  const pending = useMemo(() => pendingRequests(messages), [messages]);
  const conversation = conversationsQuery.conversations.find((c) => c.conversationId === conversationId);
  // The list is team-scoped; a just-created chat isn't in it yet (invalidate hasn't refetched), so
  // its own id bridges that one round-trip without being treated as belonging to another workspace.
  const justCreatedIdRef = useRef<string>();
  const showThread =
    Boolean(conversationId) &&
    (Boolean(conversation) || conversationsQuery.isLoading || conversationId === justCreatedIdRef.current);
  const hasMessages = Object.keys(messages).length > 0;
  const awaitingReply = isCreating || (session.isWorking && lastPromptAwaitsReply(messages));
  const showBackdrop = !showThread || awaitingReply || (!history.isLoading && !hasMessages);

  const { scrollToBottom } = useStickToBottom(scroller, content);

  // `scroller` belongs in the deps: it arrives via a ref callback after the first commit, so
  // without it this never re-runs once the node exists and the mount-path scroll does nothing.
  useEffect(() => {
    if (!history.isLoading) scrollToBottom();
  }, [conversationId, history.isLoading, scrollToBottom, scroller]);

  // A toast, not a banner: a normal, fast start never reaches 'error', so this only fires on a
  // real failure. The dependency pair fires the effect once per failure, not once per render.
  const sandboxState = session.chatSandbox?.state;
  const sandboxMessage = session.chatSandbox?.message;
  useEffect(() => {
    if (sandboxState !== 'error') return;
    toast.error({
      content: sandboxMessage ? `Sandbox failed to start: ${sandboxMessage}` : 'Sandbox failed to start',
      'data-test-id': HOME_CHAT_TEST_IDS.errorToast
    });
  }, [sandboxState, sandboxMessage]);

  useEffect(() => {
    if (conversationId && conversationId !== lastConversationId) setLastConversationId(conversationId);
  }, [conversationId, lastConversationId, setLastConversationId]);

  const resumeLastConversation =
    (location.state as Partial<typeof RESUME_LAST_CONVERSATION_STATE> | null)?.resumeLastConversation === true;
  useEffect(() => {
    if (conversationId || !resumeLastConversation || conversationsQuery.isLoading) return;
    const lastExists = conversationsQuery.conversations.some((c) => c.conversationId === lastConversationId);
    navigate(
      lastExists && lastConversationId
        ? Private_Routes.HomeChatDetail.getUrl({ conversationId: lastConversationId })
        : Private_Routes.HomeChat.getUrl(),
      { replace: true }
    );
  }, [
    conversationId,
    resumeLastConversation,
    conversationsQuery.isLoading,
    conversationsQuery.conversations,
    lastConversationId,
    navigate
  ]);

  const openConversation = usePersistentCallback((id: string) => {
    navigate(Private_Routes.HomeChatDetail.getUrl({ conversationId: id }));
  });

  const startNewChat = usePersistentCallback(() => navigate(Private_Routes.HomeChat.getUrl()));

  const togglePin = usePersistentCallback((id: string) => {
    setPinnedIds((prev = []) => (prev.includes(id) ? prev.filter((pinned) => pinned !== id) : [id, ...prev]));
  });

  const deleteConversation = usePersistentCallback(async (id: string) => {
    await remove(id);
    // A removal, not a toggle: reading-then-toggling would re-pin a deleted chat if the stored
    // preference ever held the id twice.
    setPinnedIds((prev = []) => prev.filter((pinned) => pinned !== id));
    if (id === conversationId) startNewChat();
  });

  // Single entry point for every producer of a home-chat prompt — the composer below, a widget's
  // follow-up, the Home hand-off — so all of them queue the same way when the agent is already busy
  // or the sandbox isn't confirmed ready yet. Only queues against the conversation already open: a
  // brand-new one (targetId freshly created, navigation not yet landed) is guarded separately in
  // `submit`, since there's no existing conversation's queue to hold it in yet.
  const submitOrQueueHomeChat = usePersistentCallback(
    async (targetId: string, message: string, context?: Record<string, unknown>) => {
      dispatcher.notifyUserAction();
      if (targetId === conversationId && (session.isWorking || queuedPrompts.queue.length > 0 || !sandboxReady)) {
        queuedPrompts.enqueue({ text: message, context });
        return;
      }
      await session.send(targetId, message, context);
    }
  );

  const submit = usePersistentCallback(async (value: string) => {
    const message = value.trim();
    const files = attachments.context();
    if (!message && !files) return;
    let targetId = conversationId;
    if (!targetId) {
      // Nothing to queue a brand-new chat's first prompt against — refuse outright rather than
      // create a chat the sandbox can't yet act on, or echo a prompt that never actually sends.
      if (!sandboxReady) {
        toast.error({
          content: 'DataGPT is still starting — try again in a moment.',
          'data-test-id': HOME_CHAT_TEST_IDS.errorToast
        });
        return;
      }
      const newId = nanoid();
      try {
        await create({ name: conversationNameFromPrompt(message), conversationId: newId, teamUID });
      } catch {
        return;
      }
      justCreatedIdRef.current = newId;
      targetId = newId;
      openConversation(newId);
    }
    const context = { ...mentions.contextFor(message), ...files };
    setPrompt('');
    attachments.clear();
    scrollToBottom();
    void submitOrQueueHomeChat(targetId, message, Object.keys(context).length ? context : undefined);
  });

  // Files from Home upload through the composer like any attachment; the prompt goes once they land
  // and the sandbox is ready, unless the user has edited it by then.
  // Refs, not state, guard the two side effects: an effect can run again before a state update lands.
  const handoffUploadStarted = useRef(false);
  const handoffSent = useRef(false);
  // `add` resolves once the files are queued; before that, nothing reads as uploading yet.
  const [handoffQueued, setHandoffQueued] = useState(false);
  useEffect(() => {
    if (handoff) consumePendingHomeChatPrompt();
  }, [handoff]);
  useEffect(() => {
    if (!handoff || handoffSent.current) return;
    if (handoff.files.length && !handoffQueued) {
      if (attachments.isUnavailable) {
        handoffSent.current = true;
        toast.error({
          content: 'Could not attach your files — your prompt is still in the composer',
          'data-test-id': HOME_CHAT_TEST_IDS.errorToast
        });
        return;
      }
      if (handoffUploadStarted.current || !attachments.isReady) return;
      handoffUploadStarted.current = true;
      // `.finally` alone would re-raise an `add` rejection with nothing attached to it, and would
      // still let the prompt go on to send without the files it was supposed to carry.
      void attachments
        .add(handoff.files)
        .catch((error: unknown) =>
          toast.error({
            content: error instanceof Error ? error.message : 'Could not attach your files',
            'data-test-id': HOME_CHAT_TEST_IDS.errorToast
          })
        )
        .finally(() => setHandoffQueued(true));
      return;
    }
    if (attachments.isUploading || !sandboxReady) return;
    handoffSent.current = true;
    if (handoff.prompt.trim() && prompt === handoff.prompt) void submit(handoff.prompt);
  }, [handoff, handoffQueued, attachments, submit, sandboxReady, prompt]);

  // A copied prompt lists its context keys after the separator; only its files come back, as chips.
  const restorePastedFiles = usePersistentCallback((_text: string, contextText?: string) => {
    const names = (contextText ?? '')
      .split('\n')
      .filter((key) => key.startsWith(FILE_CONTEXT_PREFIX))
      .map((key) => key.slice(FILE_CONTEXT_PREFIX.length));
    if (names.length) attachments.restore(names);
  });

  const sendFollowUp = usePersistentCallback(async (text: string) => {
    if (!conversationId) return;
    scrollToBottom();
    await submitOrQueueHomeChat(conversationId, text);
  });

  const composer = (
    <>
      <QueuedHomeChatPromptsList queue={queuedPrompts.queue} onRemove={queuedPrompts.removeAt} />
      <Composer>
        <AIChatInput
          value={prompt}
          onChange={setPrompt}
          onSubmit={submit}
          contextSeparator={CONTEXT_PATTERN_SEPARATOR}
          onPaste={restorePastedFiles}
          minHeight={COMPOSER_MIN_HEIGHT}
          placeholder={COMPOSER_PLACEHOLDER}
          isAgentWorking={session.isWorking}
          allowCancellation
          enableQueueWhileWorking
          onCancel={session.stop}
          submitDisabled={isCreating || attachments.isUploading}
          getMentionSuggestions={mentions.getMentionSuggestions}
          getMentionValue={mentions.getMentionValue}
          renderMention={mentions.renderMention}
          renderMentionSuggestion={mentions.renderMentionSuggestion}
          disableSlashCommand
          actionsAboveInput={
            attachments.items.length ? (
              <AttachmentChips items={attachments.items} onRemove={attachments.remove} />
            ) : undefined
          }
          actionsBelowInput={
            <AttachFilesButton onAdd={(files) => void attachments.add(files)} disabled={!attachments.isReady} />
          }
        />
      </Composer>
    </>
  );

  return (
    <DragDropFileUpload
      multiple
      notAllowed={!attachments.isReady}
      disabled={!attachments.isReady || attachments.isUploading}
      onDrop={(files) => void attachments.add(files)}>
      <Page direction='horizontal' width='100%'>
        <Sidebar>
          <ConversationList
            conversations={conversationsQuery.conversations}
            selectedId={conversationId}
            isLoading={conversationsQuery.isLoading}
            hasNextPage={conversationsQuery.hasNextPage}
            isFetchingNextPage={conversationsQuery.isFetchingNextPage}
            pinnedIds={pinnedIds}
            renamingId={renamingId}
            deletingId={deletingId}
            onLoadMore={() => void conversationsQuery.fetchNextPage()}
            onSelect={openConversation}
            onNewChat={startNewChat}
            onRename={rename}
            onDelete={deleteConversation}
            onTogglePin={togglePin}
          />
        </Sidebar>
        <StackItem grow='1' shrink='1' height='100%' width='0'>
          <Main height='100%' width='100%'>
            {showBackdrop ? (
              <CopilotGradientBackdrop $paused={!awaitingReply} data-test-id={HOME_CHAT_TEST_IDS.backdrop} />
            ) : null}
            <ChatHeader
              conversation={conversation}
              started={showThread && hasMessages}
              pinned={Boolean(conversationId && pinnedIds.includes(conversationId))}
              renamingId={renamingId}
              deletingId={deletingId}
              onRename={rename}
              onTogglePin={togglePin}
              onDelete={deleteConversation}
            />
            {showThread ? (
              <>
                <ThreadPane grow='1' shrink='1' width='100%'>
                  {widgetFullscreen ? <FullscreenStage data-test-id={HOME_CHAT_TEST_IDS.fullscreenStage} /> : null}
                  <Scroller ref={setScroller} height='100%' width='100%'>
                    <Column ref={setContent}>
                      {history.isLoading ? (
                        <Spinner spinning size='m' />
                      ) : (
                        <WidgetFullscreenContext.Provider value={setWidgetFullscreen}>
                          <Transcript
                            messages={messages}
                            isWorking={session.isWorking}
                            hasOlder={history.hasOlder}
                            isLoadingOlder={history.isLoadingOlder}
                            onLoadOlder={() => void history.loadOlder()}
                            widgetHost={session.widgetHost}
                            onFollowUp={sendFollowUp}
                          />
                        </WidgetFullscreenContext.Provider>
                      )}
                    </Column>
                  </Scroller>
                </ThreadPane>
                <ComposerSection pending={pending} onAnswer={session.respondToUserInput}>
                  {composer}
                </ComposerSection>
              </>
            ) : (
              <>
                <Stack grow='1' shrink='1' width='100%' align='center' alignY='center' gap={theme.spaces.x32}>
                  <CopilotLogoXl40pxIcon width={EMPTY_STATE_LOGO_SIZE} height={EMPTY_STATE_LOGO_SIZE} />
                  <EmptyStateText align='center' gap={theme.spaces.x12}>
                    <Display level='xs' weight={theme.fontWeight.medium} tone={theme.colors.gray900}>
                      Ask DataGPT anything about your data
                    </Display>
                    <Text level='sm' tone={theme.colors.gray600}>
                      It connects warehouses and files, profiles tables, builds analyses and pipelines, and keeps every
                      answer auditable.
                    </Text>
                  </EmptyStateText>
                </Stack>
                <ComposerSection>{composer}</ComposerSection>
              </>
            )}
          </Main>
        </StackItem>
      </Page>
    </DragDropFileUpload>
  );
}
